import type {
  DebugState,
  pageView,
  debugPageFeatures,
  debugPanelConfig as debugPanelConfigType,
} from 'store'
import type { DebugRequest, DebugRequestStatus } from '~/models'

type RequestToken = {
  requests: {
    [key: string]: DebugRequest
  }
}

export const useDebugStore = defineStore('debug.store', () => {
  const featuresCookies = useCookie<debugPageFeatures>('debugFeatures', {
    secure: true,
  })
  const enable = ref<boolean>(false)
  const pageView = ref<undefined | pageView>(undefined)
  const requestToken = ref<RequestToken>({
    requests: {},
  })
  const testRoutes = ref<string[] | undefined>(undefined)
  const debugPanelConfig = ref<debugPanelConfigType>({
    isExpanded: false,
    selectedTab: 'token',
    width: 300,
    height: 300,
    top: 0,
    right: 0,
    version: 3,
  })
  const debugFeatures = ref<debugPageFeatures>({
    showAnalytics: false,
    enableUiSlider: false,
    enableFlocktory: false,
  })

  const requestList = computed((): DebugRequest[] =>
    Object.values(requestToken.value.requests).slice().reverse(),
  )
  const errorRequestsList = computed((): DebugRequest[] =>
    requestList.value.filter((item) => item.status === 2),
  )

  const setDebugPanelConfig = () => {
    localStorage.setItem('debug', JSON.stringify(debugPanelConfig.value))
  }

  const getSavedDebugPanelConfig = () => {
    const savedConfig: string | null = localStorage.getItem('debug')

    if (savedConfig) {
      const config = JSON.parse(savedConfig)

      if (config?.version !== debugPanelConfig.value.version) {
        debugPanelConfig.value = {
          isExpanded: false,
          selectedTab: 'token',
          width: 300,
          height: 300,
          top: 0,
          right: 0,
          version: 3,
        }
        debugFeatures.value = {
          showAnalytics: false,
          enableUiSlider: false,
          enableFlocktory: false,
        }
      } else debugPanelConfig.value = JSON.parse(savedConfig)
    }
  }

  const setPageView = (view: Undefinable<pageView>) => {
    pageView.value = view
  }

  const toggleDebugPanel = (toggler: boolean) => {
    enable.value = toggler
  }

  const setDebugRequest = (requestId: string, requestParams: DebugRequest) => {
    if (enable.value) {
      requestToken.value.requests[requestId] = requestParams
    }
  }

  const updateDebugRequest = (
    requestId: string,
    xDebugTokenLink: string | null,
    status?: DebugRequestStatus,
    response?: any,
  ) => {
    if (enable.value) {
      requestToken.value.requests[requestId].xDebugTokenLink = xDebugTokenLink
      status && (requestToken.value.requests[requestId].status = status)
      response && (requestToken.value.requests[requestId].response = response)
    }
  }

  const setDebugFeatures = (features: DebugState['debugFeatures'] | null) => {
    if (!features) return

    debugFeatures.value = features
  }

  const toggleDebugFeature = (
    key: keyof DebugState['debugFeatures'],
    value: boolean,
  ) => {
    debugFeatures.value[key] = value

    featuresCookies.value = debugFeatures.value
  }

  return {
    enable,
    pageView,
    requestToken,
    testRoutes,
    debugPanelConfig,
    debugFeatures,
    requestList,
    errorRequestsList,
    setDebugPanelConfig,
    getSavedDebugPanelConfig,
    setPageView,
    toggleDebugPanel,
    setDebugRequest,
    updateDebugRequest,
    setDebugFeatures,
    toggleDebugFeature,
  }
})
